/**
 * Progress & Analytics Page
 * 
 * Combined view of progress tracking and analytics insights
 */

import React, { useState, useEffect } from'react'
import { Card } from'../components/ui/Card'
import { Button } from'../components/ui/Button'
import { 
 TrendingUp, RefreshCw, Target, CheckCircle2, 
 AlertTriangle, MessageCircle,
 BarChart3, Activity, Trophy, Zap, Star,
 ChevronDown, ChevronUp, Sparkles, Clock, Calendar,
 Plus
} from'lucide-react'
import { format } from'date-fns'
import { Link } from'react-router-dom'
import { useBranding } from'../hooks/useBranding'
import { RhythmSection } from '../components/analytics/RhythmSection'

// ============ Types ============

interface HierarchyItem {
 id: number
 title: string
 description?: string
 status: string
 progress: number
 item_type: string
 created_at: string
 updated_at: string
 due_date?: string
 priority?: string
}

interface HortonEngagement {
 conversations_this_period: number
 total_conversations: number
 sessions_this_period: number
 ai_requests: number
 ai_cost: number
 ai_cost_display: string
 ai_tokens: number
}

interface HierarchyBreakdownItem {
 total: number
 completed: number
 this_period: number
}

interface HierarchyOverview {
 total_items: number
 items_created_this_period: number
 breakdown: {
 dreams: HierarchyBreakdownItem
 aspirations: HierarchyBreakdownItem
 goals: HierarchyBreakdownItem
 tasks: HierarchyBreakdownItem
 action_items: HierarchyBreakdownItem
 }
}

interface CompletionData {
 total_completed: number
 overall_rate: number
 rates: {
 dreams: number
 aspirations: number
 goals: number
 tasks: number
 action_items: number
 }
}

interface RoadblockData {
 total: number
 active: number
 resolved: number
 resolution_rate: number
 by_priority: Record<string, number>
}

interface ActivityTrend {
 week: string
 created: number
 completed: number
}

interface Recommendation {
 type: string
 message: string
 icon: string
}

interface InsightsData {
 productivity_score: number
 recommendations: Recommendation[]
}

interface AnalyticsData {
 success: boolean
 period: string
 start_date: string
 end_date: string
 horton_engagement: HortonEngagement
 hierarchy_overview: HierarchyOverview
 completion: CompletionData
 roadblocks: RoadblockData
 activity_trends: ActivityTrend[]
 insights: InsightsData
}

type PeriodType ='day' |'week' |'month' |'year'
type TabType ='overview' |'items' |'insights'

// ============ Component ============

export const ProgressAnalytics: React.FC = () => {
 const branding = useBranding()
 const [loading, setLoading] = useState(true)
 const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null)
 // Failures get their own state (2026-08-29): a failed load used to render the
 // same "No items found" / blank tab as a genuinely empty account.
 const [error, setError] = useState<string | null>(null)
 const [itemsError, setItemsError] = useState<string | null>(null)
 const [items, setItems] = useState<HierarchyItem[]>([])
 const [period, setPeriod] = useState<PeriodType>('month')
 const [activeTab, setActiveTab] = useState<TabType>('overview')
 const [selectedType, setSelectedType] = useState<string>('all')
 const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['hierarchy','trends']))

 // Fetch all data
 const fetchData = async () => {
 try {
 setLoading(true)
 setError(null)
 setItemsError(null)

 // Same-origin paths (2026-08-29): these were hardcoded to
 // http://localhost:5001, which is dead in production and on any phone.
 const analyticsResponse = await fetch(`/api/user-analytics/overview?period=${period}`, {
 credentials:'include'
 })

 if (analyticsResponse.ok) {
 const result = await analyticsResponse.json()
 if (result.success) {
 setAnalyticsData(result)
 } else {
 setAnalyticsData(null)
 setError("We couldn't load your analytics just now.")
 }
 } else {
 setAnalyticsData(null)
 setError(`We couldn't load your analytics (server said ${analyticsResponse.status}).`)
 }

 // Fetch all hierarchy items for the items tab
 const allItems: HierarchyItem[] = []

 // A swallowed failure used to look identical to an empty account, so
 // remember when we could not look at all (2026-08-29).
 let itemsFailed = false
 const getJson = async (path: string) => {
 try {
 const res = await fetch(path, { credentials:'include' })
 if (!res.ok) {
 itemsFailed = true
 return { success: false }
 }
 return await res.json()
 } catch {
 itemsFailed = true
 return { success: false }
 }
 }

 const fetchPromises = [
 getJson('/api/hierarchy/dreams')
 .then(data => {
 if (data.success && data.dreams) {
 allItems.push(...data.dreams.map((d: any) => ({
 ...d, item_type:'dream', progress: d.progress || 0
 })))
 }
 }),

 getJson('/api/hierarchy/aspirations')
 .then(data => {
 if (data.success && data.aspirations) {
 allItems.push(...data.aspirations.map((a: any) => ({
 ...a, item_type:'aspiration', progress: a.progress || 0
 })))
 }
 }),

 getJson('/api/hierarchy/goals')
 .then(data => {
 if (data.success && data.goals) {
 allItems.push(...data.goals.map((g: any) => ({
 ...g, item_type:'goal', progress: g.progress || 0
 })))
 }
 }),

 getJson('/api/hierarchy/tasks')
 .then(data => {
 if (data.success && data.tasks) {
 allItems.push(...data.tasks.map((t: any) => ({
 ...t, item_type:'task', progress: t.progress || 0
 })))
 }
 }),

 getJson('/api/hierarchy/action-items')
 .then(data => {
 if (data.success && data.action_items) {
 allItems.push(...data.action_items.map((a: any) => ({
 ...a, item_type:'action_item', progress: a.progress || 0
 })))
 }
 })
 ]

 await Promise.all(fetchPromises)
 allItems.sort((a, b) => new Date(b.updated_at || b.created_at).getTime() - new Date(a.updated_at || a.created_at).getTime())
 setItems(allItems)
 if (itemsFailed) {
 setItemsError("We couldn't load all of your items.")
 }

 } catch (err) {
 console.error('Error fetching data:', err)
 setAnalyticsData(null)
 setError("We couldn't reach the server.")
 setItemsError("We couldn't reach the server.")
 } finally {
 setLoading(false)
 }
 }

 useEffect(() => {
 fetchData()
 }, [period])

 // Utility functions
 const toggleSection = (section: string) => {
 setExpandedSections(prev => {
 const next = new Set(prev)
 if (next.has(section)) next.delete(section)
 else next.add(section)
 return next
 })
 }

 const formatNumber = (num: number) => new Intl.NumberFormat('en-US').format(num)

 const getCompletionColor = (rate: number) => {
 if (rate >= 70) return'text-ok'
 if (rate >= 40) return'text-warn'
 return'text-bad'
 }

 const getProgressBarColor = (rate: number) => {
 if (rate >= 70) return'bg-ok'
 if (rate >= 40) return'bg-warn'
 return'bg-bad'
 }

 const getProgressColor = (progress: number) => {
 if (progress >= 75) return'bg-ok'
 if (progress >= 50) return'bg-brand'
 if (progress >= 25) return'bg-warn'
 return'bg-soft'
 }

 const getProgressTextColor = (progress: number) => {
 if (progress >= 75) return'text-ok'
 if (progress >= 50) return'text-brand'
 if (progress >= 25) return'text-warn'
 return'text-secondary'
 }

 // getProductivityGrade removed 2026-08-29: a letter grade ("D") on a
 // Productivity Score scores the user's life. See the period summary card.

 const getStatusIcon = (status: string) => {
 switch (status) {
 case'completed': return <CheckCircle2 className="w-5 h-5 text-ok" />
 case'in_progress': return <Activity className="w-5 h-5 text-brand" />
 default: return <Clock className="w-5 h-5 text-muted" />
 }
 }

 const getItemTypeColor = (type: string) => {
 switch (type) {
 case'dream': return'bg-brand-subtle text-brand'
 case'aspiration': return'bg-brand-subtle text-brand'
 case'goal': return'bg-brand-subtle text-brand'
 case'task': return'bg-ok/10 text-ok'
 case'action_item': return'bg-warn/10 text-warn'
 default: return'bg-surface-alt text-primary'
 }
 }

 const getItemTypeLink = (type: string, id: number) => {
 switch (type) {
 case'dream': return`/dreams/${id}`
 case'goal': return`/goals/${id}`
 case'task': return`/tasks/${id}`
 default: return'#'
 }
 }

 const filteredItems = selectedType ==='all' 
 ? items 
 : items.filter(item => item.item_type === selectedType)

 // Loading state
 if (loading) {
 return (
 <div className="min-h-screen bg-default p-8">
 <div className="max-w-7xl mx-auto">
 <div className="flex items-center justify-center h-64">
 <RefreshCw className="w-8 h-8 text-brand animate-spin" />
 </div>
 </div>
 </div>
 )
 }

 const totalItems = items.length
 const completedItems = items.filter(i => i.status ==='completed').length
 const inProgressItems = items.filter(i => i.status ==='in_progress').length

 return (
 <div className="min-h-screen bg-default p-8">
 <div className="max-w-7xl mx-auto">
 {/* Header */}
 <div className="mb-8">
 <div className="flex items-center justify-between mb-4">
 <div>
 <div className="flex items-center gap-3 mb-2">
 <div className="p-2 bg-brand rounded-xl">
 <TrendingUp className="w-8 h-8 text-on-brand" />
 </div>
 <h1 className="text-2xl font-semibold text-brand">
 Progress & Analytics
 </h1>
 </div>
 <p className="text-secondary">
 Track your progress, engagement, and achievements
 </p>
 </div>
 <div className="flex items-center gap-4">
 {/* Period Selector */}
 <div className="flex bg-surface rounded-xl shadow-sm border border-soft overflow-hidden">
 {(['day','week','month','year'] as PeriodType[]).map((p) => (
 <button
 key={p}
 onClick={() => setPeriod(p)}
 className={`px-4 py-2 min-h-touch text-sm font-medium transition-all ${
 period === p
 ?'bg-brand text-on-brand'
 :'text-primary hover:bg-surface-alt'
 }`}
 >
 {p.charAt(0).toUpperCase() + p.slice(1)}
 </button>
 ))}
 </div>
 
 <Button onClick={fetchData} variant="outline" className="flex items-center gap-2">
 <RefreshCw className="w-4 h-4" />
 Refresh
 </Button>
 </div>
 </div>
 
 {analyticsData && (
 <div className="text-sm text-secondary">
 {format(new Date(analyticsData.start_date),'MMM dd, yyyy')} - {format(new Date(analyticsData.end_date),'MMM dd, yyyy')}
 </div>
 )}
 </div>

 {/* An honest failure, never a quiet blank tab (2026-08-29) */}
 {error && (
 <Card className="p-4 mb-8 border border-warn/30 bg-warn/10">
 <div className="flex flex-wrap items-center justify-between gap-3">
 <div className="flex items-center gap-2 text-warn">
 <AlertTriangle className="w-5 h-5 shrink-0" />
 <span>{error} Nothing has been lost — we just couldn't read it.</span>
 </div>
 <Button onClick={fetchData} variant="outline" className="flex items-center gap-2 min-h-touch">
 <RefreshCw className="w-4 h-4" />
 Try again
 </Button>
 </div>
 </Card>
 )}

 {/* Period summary — 2026-08-29: replaced a letter-graded "Productivity
 Score: n/100". The product does not grade the user's life, so this
 says what they actually did this period instead of scoring it. */}
 {analyticsData && (
 <Card className="p-6 mb-8 bg-surface-alt border border-soft">
 <div className="flex flex-wrap items-center justify-between gap-6">
 <div>
 <div className="text-xl font-semibold text-primary">
 This {period}, so far
 </div>
 <div className="text-secondary mt-1">
 {formatNumber(completedItems)} finished · {formatNumber(analyticsData.hierarchy_overview.items_created_this_period)} added · {formatNumber(analyticsData.horton_engagement.conversations_this_period)} conversations with {branding.name}
 </div>
 </div>
 <Trophy className="w-12 h-12 text-brand opacity-40" />
 </div>
 </Card>
 )}

 {/* Tab Navigation */}
 <div className="flex gap-2 mb-6">
 {[
 { id:'overview', label:'Overview', icon: BarChart3 },
 { id:'items', label:'All Items', icon: Target },
 { id:'insights', label:'Insights', icon: Sparkles }
 ].map(tab => (
 <button
 key={tab.id}
 onClick={() => setActiveTab(tab.id as TabType)}
 className={`flex items-center gap-2 px-4 py-2 min-h-touch rounded-lg font-medium transition-all ${
 activeTab === tab.id
 ?'bg-brand text-on-brand shadow-lg'
 :'bg-surface text-primary hover:bg-surface-alt'
 }`}
 >
 <tab.icon className="w-4 h-4" />
 {tab.label}
 </button>
 ))}
 </div>

 {/* Overview Tab */}
 {activeTab ==='overview' && analyticsData && (
 <>
 {/* Your rhythm (2026-09-15): when you do things, when you are around, when you are quiet */}
 <RhythmSection />
 {/* Main Stats Cards */}
 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
 <Card className="p-6 bg-brand text-on-brand shadow-lg">
 <div className="flex items-center justify-between mb-3">
 <Target className="w-8 h-8 opacity-80" />
 <span className="text-sm opacity-90">Total Items</span>
 </div>
 <div className="text-4xl font-bold">{formatNumber(totalItems)}</div>
 <div className="text-sm opacity-90 mt-2 flex items-center gap-1">
 <Zap className="w-4 h-4" />
 +{analyticsData.hierarchy_overview.items_created_this_period} this {period}
 </div>
 </Card>

 <Card className="p-6 bg-ok text-on-brand shadow-lg">
 <div className="flex items-center justify-between mb-3">
 <CheckCircle2 className="w-8 h-8 opacity-80" />
 <span className="text-sm opacity-90">Completed</span>
 </div>
 <div className="text-4xl font-bold">{formatNumber(completedItems)}</div>
 {/* 2026-08-29: was "{rate}% completion rate" — the same scoring frame
 the graded Productivity Score was retired for. Counts, not a score. */}
 <div className="text-sm opacity-90 mt-2">
 of {formatNumber(totalItems)} on your map
 </div>
 </Card>

 <Card className="p-6 bg-brand text-on-brand shadow-lg">
 <div className="flex items-center justify-between mb-3">
 <MessageCircle className="w-8 h-8 opacity-80" />
 <span className="text-sm opacity-90">{branding.name} Chats</span>
 </div>
 <div className="text-4xl font-bold">{formatNumber(analyticsData.horton_engagement.conversations_this_period)}</div>
 <div className="text-sm opacity-90 mt-2">
 {formatNumber(analyticsData.horton_engagement.total_conversations)} total
 </div>
 </Card>

 <Card className="p-6 bg-warn text-on-brand shadow-lg">
 <div className="flex items-center justify-between mb-3">
 <Activity className="w-8 h-8 opacity-80" />
 <span className="text-sm opacity-90">In Progress</span>
 </div>
 <div className="text-4xl font-bold">{inProgressItems}</div>
 <div className="text-sm opacity-90 mt-2">actively being worked on</div>
 </Card>
 </div>

 {/* Hierarchy Overview */}
 <Card className="mb-8 overflow-hidden">
 <button 
 onClick={() => toggleSection('hierarchy')}
 className="w-full p-6 flex items-center justify-between bg-surface-alt hover:bg-surface-alt transition-all"
 >
 <div className="flex items-center gap-3">
 <BarChart3 className="w-6 h-6 text-brand" />
 <span className="text-lg font-semibold text-primary">Progress by Category</span>
 </div>
 {expandedSections.has('hierarchy') ? <ChevronUp className="w-5 h-5 text-secondary" /> : <ChevronDown className="w-5 h-5 text-secondary" />}
 </button>
 
 {expandedSections.has('hierarchy') && (
 <div className="p-6">
 <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
 {Object.entries(analyticsData.hierarchy_overview.breakdown).map(([type, item]) => (
 <div 
 key={type}
 className="p-4 rounded-xl bg-surface-alt border border-soft"
 >
 <div className="flex items-center justify-between mb-3">
 <span className="text-sm font-medium text-secondary capitalize">
 {type.replace('_','')}
 </span>
 <span className={`text-sm font-bold ${getCompletionColor(analyticsData.completion.rates[type as keyof typeof analyticsData.completion.rates])}`}>
 {analyticsData.completion.rates[type as keyof typeof analyticsData.completion.rates]?.toFixed(0)}%
 </span>
 </div>
 <div className="text-xl font-semibold text-primary mb-2">{item.total}</div>
 <div className="w-full bg-surface-alt rounded-full h-2 mb-2">
 <div 
 className={`h-2 rounded-full transition-all ${getProgressBarColor(analyticsData.completion.rates[type as keyof typeof analyticsData.completion.rates])}`}
 style={{ width:`${Math.min(100, analyticsData.completion.rates[type as keyof typeof analyticsData.completion.rates] || 0)}%` }}
 />
 </div>
 <div className="flex items-center justify-between text-xs text-secondary">
 <span>{item.completed} done</span>
 <span>+{item.this_period} new</span>
 </div>
 </div>
 ))}
 </div>
 </div>
 )}
 </Card>

 {/* Activity Trends & Roadblocks */}
 <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
 <Card className="p-6">
 <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
 <Activity className="w-5 h-5 text-brand" />
 Activity Trends
 </h3>
 {analyticsData.activity_trends.length > 0 ? (
 <div className="space-y-3">
 {analyticsData.activity_trends.map((trend, idx) => (
 <div key={idx} className="flex items-center gap-4">
 <div className="w-20 text-sm text-secondary">
 {format(new Date(trend.week),'MMM dd')}
 </div>
 <div className="flex-1 flex items-center gap-2">
 <div className="flex-1 bg-surface-alt rounded-full h-3 overflow-hidden">
 <div 
 className="h-3 bg-brand rounded-full"
 style={{ width:`${Math.min(100, trend.created * 10)}%` }}
 />
 </div>
 <span className="w-6 text-xs font-medium text-brand">{trend.created}</span>
 </div>
 <div className="flex-1 flex items-center gap-2">
 <div className="flex-1 bg-surface-alt rounded-full h-3 overflow-hidden">
 <div 
 className="h-3 bg-ok rounded-full"
 style={{ width:`${Math.min(100, trend.completed * 10)}%` }}
 />
 </div>
 <span className="w-6 text-xs font-medium text-ok">{trend.completed}</span>
 </div>
 </div>
 ))}
 <div className="flex items-center justify-end gap-6 mt-4 text-xs">
 <div className="flex items-center gap-2">
 <div className="w-3 h-3 bg-brand rounded-full" />
 <span className="text-secondary">Created</span>
 </div>
 <div className="flex items-center gap-2">
 <div className="w-3 h-3 bg-ok rounded-full" />
 <span className="text-secondary">Completed</span>
 </div>
 </div>
 </div>
 ) : (
 <div className="text-center text-secondary py-8">
 No activity data for this period
 </div>
 )}
 </Card>

 <Card className="p-6">
 <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
 <AlertTriangle className="w-5 h-5 text-warn" />
 Roadblocks
 </h3>
 <div className="grid grid-cols-2 gap-4 mb-4">
 <div className="p-4 bg-warn/10 rounded-xl">
 <div className="text-2xl font-semibold text-warn">{analyticsData.roadblocks.active}</div>
 <div className="text-sm text-warn">Active</div>
 </div>
 <div className="p-4 bg-ok/10 rounded-xl">
 <div className="text-2xl font-semibold text-ok">{analyticsData.roadblocks.resolved}</div>
 <div className="text-sm text-ok">Resolved</div>
 </div>
 </div>
 
 <div className="mb-4">
 <div className="flex items-center justify-between mb-2">
 <span className="text-sm text-secondary">Resolution Rate</span>
 <span className={`text-sm font-bold ${getCompletionColor(analyticsData.roadblocks.resolution_rate)}`}>
 {analyticsData.roadblocks.resolution_rate.toFixed(1)}%
 </span>
 </div>
 <div className="w-full bg-surface-alt rounded-full h-3">
 <div 
 className={`h-3 rounded-full ${getProgressBarColor(analyticsData.roadblocks.resolution_rate)}`}
 style={{ width:`${analyticsData.roadblocks.resolution_rate}%` }}
 />
 </div>
 </div>
 
 {Object.keys(analyticsData.roadblocks.by_priority).length > 0 && (
 <div className="flex gap-2 flex-wrap">
 {Object.entries(analyticsData.roadblocks.by_priority).map(([priority, count]) => (
 <span 
 key={priority}
 className={`px-3 py-1 rounded-full text-sm font-medium ${
 priority ==='high' ?'bg-bad/10 text-bad' :
 priority ==='medium' ?'bg-warn/10 text-warn' :
'bg-surface-alt text-primary'
 }`}
 >
 {priority}: {count}
 </span>
 ))}
 </div>
 )}
 </Card>
 </div>

 {/* Persona name comes from the active brand, not a hardcoded a literal (2026-08-29) */}
 <Card className="p-6">
 <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
 <MessageCircle className="w-5 h-5 text-brand" />
 {branding.name} AI Engagement
 </h3>
 <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
 <div className="p-4 bg-brand-subtle rounded-xl">
 <div className="text-xl font-semibold text-brand">{analyticsData.horton_engagement.conversations_this_period}</div>
 <div className="text-sm text-brand">Conversations</div>
 </div>
 <div className="p-4 bg-brand-subtle rounded-xl">
 <div className="text-xl font-semibold text-brand">{analyticsData.horton_engagement.sessions_this_period}</div>
 <div className="text-sm text-brand">Sessions</div>
 </div>
 <div className="p-4 bg-ok/10 rounded-xl">
 <div className="text-xl font-semibold text-ok">{analyticsData.horton_engagement.ai_requests}</div>
 <div className="text-sm text-ok">AI Requests</div>
 </div>
 <div className="p-4 bg-warn/10 rounded-xl">
 <div className="text-xl font-semibold text-warn">{analyticsData.horton_engagement.ai_cost_display}</div>
 <div className="text-sm text-warn">AI Cost</div>
 </div>
 </div>
 </Card>
 </>
 )}

 {/* Items Tab */}
 {activeTab ==='items' && (
 <>
 {/* Filter Bar */}
 <Card className="p-4 mb-6">
 <div className="flex items-center gap-4 flex-wrap">
 <span className="text-sm font-medium text-secondary">Filter:</span>
 {['all','dream','aspiration','goal','task','action_item'].map(type => (
 <button
 key={type}
 onClick={() => setSelectedType(type)}
 className={`px-4 py-2 min-h-touch rounded-lg text-sm font-medium transition-all ${
 selectedType === type
 ?'bg-brand-subtle text-brand'
 :'bg-surface-alt text-secondary hover:bg-surface-alt'
 }`}
 >
 {type ==='all' ?'All' : type ==='action_item' ?'Actions' : type.charAt(0).toUpperCase() + type.slice(1) +'s'}
 </button>
 ))}
 </div>
 </Card>

 {/* Some lists loaded, some did not — say so rather than showing a
 short list as if it were everything (2026-08-29) */}
 {itemsError && filteredItems.length > 0 && (
 <Card className="p-4 mb-6 border border-warn/30 bg-warn/10">
 <div className="flex flex-wrap items-center justify-between gap-3">
 <div className="flex items-center gap-2 text-warn">
 <AlertTriangle className="w-5 h-5 shrink-0" />
 <span>{itemsError} This list may be missing some of them.</span>
 </div>
 <Button onClick={fetchData} variant="outline" className="min-h-touch">
 <RefreshCw className="w-4 h-4 mr-2" />
 Try again
 </Button>
 </div>
 </Card>
 )}

 {/* Items List */}
 <Card className="p-6">
 <h3 className="text-lg font-semibold text-primary mb-6 flex items-center gap-2">
 <Target className="w-5 h-5 text-brand" />
 All Items ({filteredItems.length})
 </h3>
 
 {filteredItems.length > 0 ? (
 <div className="space-y-4">
 {filteredItems.map((item) => (
 <Link 
 key={`${item.item_type}-${item.id}`}
 to={getItemTypeLink(item.item_type, item.id)}
 className="block"
 >
 <div className="p-4 rounded-xl bg-surface border border-soft hover:shadow-md hover:border-brand-subtle transition-all">
 <div className="flex items-start justify-between mb-3">
 <div className="flex items-start gap-3">
 {getStatusIcon(item.status)}
 <div>
 <h4 className="font-semibold text-primary">{item.title}</h4>
 {item.description && (
 <p className="text-sm text-secondary mt-1 line-clamp-1">{item.description}</p>
 )}
 </div>
 </div>
 <span className={`px-3 py-1 rounded-full text-xs font-medium ${getItemTypeColor(item.item_type)}`}>
 {item.item_type ==='action_item' ?'action' : item.item_type}
 </span>
 </div>
 
 <div className="flex items-center gap-4">
 <div className="flex-1">
 <div className="w-full bg-surface-alt rounded-full h-3 overflow-hidden">
 <div 
 className={`h-3 rounded-full transition-all ${getProgressColor(item.progress || 0)}`}
 style={{ width:`${item.progress || 0}%` }}
 />
 </div>
 </div>
 <span className={`text-sm font-bold min-w-[3rem] text-right ${getProgressTextColor(item.progress || 0)}`}>
 {item.progress || 0}%
 </span>
 </div>
 
 <div className="flex items-center justify-between mt-3 text-xs text-secondary">
 <span className="flex items-center gap-1">
 <Calendar className="w-3 h-3" />
 {item.created_at ? format(new Date(item.created_at),'MMM dd, yyyy') :'No date'}
 </span>
 <span className={`px-2 py-0.5 rounded-full ${
 item.status ==='completed' ?'bg-ok/10 text-ok' :
 item.status ==='in_progress' ?'bg-brand-subtle text-brand' :
'bg-surface-alt text-secondary'
 }`}>
 {item.status?.replace('_','') ||'pending'}
 </span>
 </div>
 </div>
 </Link>
 ))}
 </div>
 ) : itemsError ? (
 // Not an empty state (2026-08-29): we could not look, so we must not
 // claim there is nothing here.
 <div className="text-center py-12">
 <AlertTriangle className="w-16 h-16 text-warn mx-auto mb-4" />
 <h4 className="text-lg font-semibold text-primary mb-2">We couldn't load your items</h4>
 <p className="text-secondary mb-4">{itemsError} They are still there — this is on us.</p>
 <Button onClick={fetchData} variant="outline" className="min-h-touch">
 <RefreshCw className="w-4 h-4 mr-2" />
 Try again
 </Button>
 </div>
 ) : (
 <div className="text-center py-12">
 <Target className="w-16 h-16 text-muted mx-auto mb-4" />
 <h4 className="text-lg font-semibold text-secondary mb-2">No items found</h4>
 <p className="text-secondary mb-4">Start by creating dreams, goals, or tasks</p>
 <Link to="/horton-ai">
 <Button className="bg-brand text-on-brand">
 <Plus className="w-4 h-4 mr-2" />
 Chat with {branding.name}
 </Button>
 </Link>
 </div>
 )}
 </Card>
 </>
 )}

 {/* Insights Tab */}
 {activeTab ==='insights' && analyticsData && (
 <Card className="p-6">
 <h3 className="text-lg font-semibold text-primary mb-6 flex items-center gap-2">
 <Sparkles className="w-5 h-5 text-brand" />
 AI Insights & Recommendations
 </h3>
 
 {analyticsData.insights.recommendations.length > 0 ? (
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 {analyticsData.insights.recommendations.map((rec, idx) => (
 <div 
 key={idx}
 className={`p-4 rounded-xl border-l-4 ${
 rec.type ==='praise' ?'bg-ok/10 border-ok' :
 rec.type ==='start' ?'bg-brand-subtle border-brand' :
 rec.type ==='focus' ?'bg-warn/10 border-warn' :
 rec.type ==='roadblocks' ?'bg-bad/10 border-bad' :
'bg-brand-subtle border-brand'
 }`}
 >
 <div className="flex items-start gap-3">
 <span className="text-2xl">{rec.icon}</span>
 <p className="text-primary">{rec.message}</p>
 </div>
 </div>
 ))}
 </div>
 ) : (
 <div className="text-center py-12">
 <Star className="w-12 h-12 text-warn mx-auto mb-4" />
 <p className="text-secondary">
 Great job! Keep up the good work and check back for personalized recommendations.
 </p>
 </div>
 )}
 </Card>
 )}
 </div>
 </div>
 )
}

